import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { Trash, ShieldAlert, Users, ShoppingBag, DollarSign } from 'lucide-react';

export default function AdminDashboard() {
  const { user } = useAuth();
  const [stats, setStats] = useState({});
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      
      const [statsRes, usersRes] = await Promise.all([
        axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/admin/stats`, { headers }),
        axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/admin/users`, { headers })
      ]);
      
      setStats(statsRes.data);
      setUsers(usersRes.data);
    } catch (error) {
      toast.error("Failed to load admin data");
    } finally {
      setLoading(false);
    }
  };

  const deleteUser = async (userId) => {
    if (!window.confirm("Permanently delete this user and all their data?")) return;
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${process.env.REACT_APP_BACKEND_URL}/api/admin/users/${userId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("User deleted");
      fetchData();
    } catch (error) {
      toast.error("Delete failed");
    }
  };

  if (!user || user.role !== 'admin') {
    return <div className="p-8 text-center">Access Denied</div>;
  }

  return (
    <div className="max-w-7xl mx-auto px-4 py-8">
      <h1 className="text-3xl font-bold mb-8 flex items-center gap-3">
        <ShieldAlert className="text-red-600" /> Super Admin Panel
      </h1>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-12">
        <StatCard title="Total Users" value={stats.total_users} icon={<Users className="text-blue-500" />} />
        <StatCard title="Sellers" value={stats.total_sellers} icon={<Users className="text-orange-500" />} />
        <StatCard title="Products" value={stats.total_products} icon={<ShoppingBag className="text-green-500" />} />
        <StatCard title="Total Revenue" value={`${stats.total_revenue} DZD`} icon={<DollarSign className="text-yellow-500" />} />
      </div>

      {/* User Management */}
      <div className="bg-white rounded-xl border border-stone-200 shadow-sm overflow-hidden">
        <div className="p-6 border-b border-stone-100">
          <h2 className="text-xl font-bold">User Management</h2>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-left">
            <thead className="bg-stone-50 text-stone-500">
              <tr>
                <th className="p-4">Name</th>
                <th className="p-4">Email</th>
                <th className="p-4">Role</th>
                <th className="p-4">Joined</th>
                <th className="p-4">Actions</th>
              </tr>
            </thead>
            <tbody>
              {users.map(u => (
                <tr key={u.id} className="border-b border-stone-100 hover:bg-stone-50/50">
                  <td className="p-4 font-medium">{u.name}</td>
                  <td className="p-4 text-stone-500">{u.email}</td>
                  <td className="p-4">
                    <span className={`px-2 py-1 rounded-full text-xs font-bold ${
                      u.role === 'admin' ? 'bg-red-100 text-red-800' : 
                      u.role === 'seller' ? 'bg-orange-100 text-orange-800' : 'bg-blue-100 text-blue-800'
                    }`}>
                      {u.role.toUpperCase()}
                    </span>
                  </td>
                  <td className="p-4 text-stone-400 text-sm">
                    {new Date(u.created_at).toLocaleDateString()}
                  </td>
                  <td className="p-4">
                    {u.role !== 'admin' && (
                      <Button variant="destructive" size="sm" onClick={() => deleteUser(u.id)}>
                        <Trash size={16} />
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

const StatCard = ({ title, value, icon }) => (
  <div className="bg-white p-6 rounded-xl border border-stone-200 shadow-sm flex items-center justify-between">
    <div>
      <p className="text-stone-500 text-sm font-medium uppercase tracking-wide">{title}</p>
      <p className="text-2xl font-bold text-stone-900 mt-1">{value}</p>
    </div>
    <div className="bg-stone-50 p-3 rounded-full">{icon}</div>
  </div>
);
